import React from 'react';
import { Link } from 'react-router-dom';
import { FaRocket, FaClock, FaBookOpen, FaHeadphones, FaUsers, FaStar, FaCheck, FaPlay, FaGraduationCap, FaLightbulb, FaGlobe, FaHeart } from 'react-icons/fa';
import SEO from '../components/SEO';

const LearnFarsiFast: React.FC = () => {
  const fastLearningTips = [
    {
      icon: <FaRocket className="w-8 h-8 text-blue-600" />,
      title: "Immerse Yourself Daily",
      description: "Spend at least 30 minutes daily with Persian content - lessons, music, podcasts, or conversations.",
      tips: [
        "Listen to Persian music while commuting",
        "Watch Iranian movies with subtitles",
        "Practice with native speakers in our community"
      ]
    },
    {
      icon: <FaClock className="w-8 h-8 text-green-600" />,
      title: "Use Spaced Repetition",
      description: "Our intelligent flashcard system helps you remember vocabulary efficiently.",
      tips: [
        "Review words at optimal intervals",
        "Focus on high-frequency vocabulary first",
        "Practice pronunciation with audio feedback"
      ]
    },
    {
      icon: <FaBookOpen className="w-8 h-8 text-purple-600" />,
      title: "Learn Grammar in Context",
      description: "Understand Persian grammar through real conversations, not just rules.",
      tips: [
        "Study grammar through dialogue examples",
        "Practice with interactive exercises",
        "Learn cultural context behind expressions"
      ]
    },
    {
      icon: <FaHeadphones className="w-8 h-8 text-orange-600" />,
      title: "Master Pronunciation Early",
      description: "Get pronunciation right from the start with our audio-guided lessons.",
      tips: [
        "Practice with native speaker recordings",
        "Use our pronunciation feedback system",
        "Learn the Persian alphabet thoroughly"
      ]
    }
  ];

  const successStories = [
    {
      name: "Sarah Johnson",
      timeframe: "3 months",
      achievement: "Basic conversations",
      story: "I went from zero to having basic conversations with my Persian friends in just 3 months using Pirooz's structured approach."
    },
    {
      name: "David Chen",
      timeframe: "6 months",
      achievement: "Intermediate level",
      story: "The daily practice and cultural context helped me reach intermediate level faster than I expected."
    },
    {
      name: "Maria Rodriguez",
      timeframe: "1 year",
      achievement: "Advanced proficiency",
      story: "Combining lessons with real conversations in the community accelerated my learning significantly."
    }
  ];

  const learningPath = [
    {
      week: "Weeks 1-2",
      focus: "Persian Alphabet & Basic Sounds",
      goals: ["Master the 32 letters", "Learn basic pronunciation", "Read simple words"],
      resources: ["Alphabet learning pathway", "Pronunciation guides", "Basic vocabulary"]
    },
    {
      week: "Weeks 3-4",
      focus: "Essential Vocabulary & Greetings",
      goals: ["Learn 100+ common words", "Master greetings and introductions", "Practice basic conversations"],
      resources: ["Vocabulary builder", "Conversation lessons", "Flashcards"]
    },
    {
      week: "Weeks 5-8",
      focus: "Basic Grammar & Sentence Structure",
      goals: ["Understand word order", "Learn present tense verbs", "Form simple sentences"],
      resources: ["Grammar fundamentals", "Interactive exercises", "Writing practice"]
    },
    {
      week: "Weeks 9-12",
      focus: "Conversational Skills & Cultural Context",
      goals: ["Hold basic conversations", "Understand cultural nuances", "Read simple texts"],
      resources: ["Cultural lessons", "Real conversation practice", "Reading materials"]
    }
  ];

  return (
    <>
      <SEO 
        title="Learn Farsi Fast in 30 Days | Persian Language Learning | پیروز Pirooz"
        description="Learn Farsi fast with our proven 30-day method. Master Persian language quickly with interactive lessons, spaced repetition, and cultural immersion. Start speaking Persian today!"
        keywords={[
          // Primary Target Keywords
          'learn farsi fast', 'how to learn farsi fast', 'learn farsi in 30 days',
          'fastest way to learn farsi', 'quick farsi learning', 'accelerated persian course',
          'learn persian fast', 'how to learn persian fast', 'learn persian in 30 days',
          'fastest way to learn persian', 'quick persian learning', 'accelerated farsi course',
          
          // Secondary Variations
          'speed up farsi learning', 'rapid persian learning', 'fast farsi course',
          'quick persian lessons', 'accelerated farsi lessons', 'fast persian lessons',
          'learn farsi quickly', 'learn persian quickly', 'fast farsi learning method',
          'rapid persian learning method', 'accelerated farsi learning', 'fast persian learning',
          
          // Method-Specific Keywords
          'farsi learning techniques', 'persian learning techniques', 'farsi study methods',
          'persian study methods', 'farsi learning strategies', 'persian learning strategies',
          'farsi learning tips', 'persian learning tips', 'farsi learning hacks',
          'persian learning hacks', 'farsi learning shortcuts', 'persian learning shortcuts',
          
          // Time-Based Keywords
          'learn farsi in 1 month', 'learn persian in 1 month', '30 day farsi challenge',
          '30 day persian challenge', 'farsi in 30 days', 'persian in 30 days',
          'one month farsi course', 'one month persian course', 'farsi bootcamp',
          'persian bootcamp', 'intensive farsi course', 'intensive persian course',
          
          // Competitor Keywords
          'farsi language immersion', 'persian language immersion', 'farsi intensive program',
          'persian intensive program', 'farsi crash course', 'persian crash course',
          'farsi language bootcamp', 'persian language bootcamp', 'farsi intensive learning',
          'persian intensive learning', 'farsi rapid progress', 'persian rapid progress',
          
          // Related Keywords
          'farsi learning motivation', 'persian learning motivation', 'farsi learning goals',
          'persian learning goals', 'farsi learning schedule', 'persian learning schedule',
          'farsi daily practice', 'persian daily practice', 'farsi learning routine',
          'persian learning routine', 'farsi study plan', 'persian study plan',
          
          // Success Keywords
          'farsi learning success', 'persian learning success', 'farsi fluency fast',
          'persian fluency fast', 'farsi speaking fast', 'persian speaking fast',
          'farsi conversation fast', 'persian conversation fast', 'farsi proficiency fast',
          'persian proficiency fast', 'farsi mastery fast', 'persian mastery fast'
        ]}
        canonicalUrl="/learn-farsi-fast"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "Course",
          "name": "Learn Farsi Fast - 30 Day Persian Language Course",
          "description": "Accelerated Persian language learning course designed to get you speaking Farsi in 30 days",
          "provider": {
            "@type": "EducationalOrganization",
            "name": "پیروز Pirooz"
          },
          "educationalLevel": "Beginner to Intermediate",
          "inLanguage": ["en", "fa"],
          "courseCode": "FARSI-FAST-30",
          "timeRequired": "PT30D",
          "educationalCredentialAwarded": "Certificate of Completion",
          "hasCourseInstance": {
            "@type": "CourseInstance",
            "courseMode": "online",
            "inLanguage": ["en", "fa"],
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            }
          }
        }}
      />
      
      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <div className="bg-gradient-to-br from-blue-600 to-purple-700 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
            <div className="text-center">
              <h1 className="text-4xl md:text-6xl font-bold mb-6">
                Learn Farsi Fast
                <span className="block text-2xl md:text-3xl mt-2 text-blue-200">
                  Master Persian in 30 Days
                </span>
              </h1>
              <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto">
                Discover the fastest way to learn Persian with our proven method combining 
                <span className="font-semibold"> spaced repetition</span>, 
                <span className="font-semibold"> cultural immersion</span>, and 
                <span className="font-semibold"> real conversations</span>
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link 
                  to="/alphabet-learning" 
                  className="px-8 py-4 bg-white text-blue-600 font-bold rounded-lg hover:bg-gray-100 transition duration-300 text-lg"
                >
                  Start Learning Now
                </Link>
                <Link 
                  to="/pathways" 
                  className="px-8 py-4 border-2 border-white text-white font-bold rounded-lg hover:bg-white hover:text-blue-600 transition duration-300 text-lg"
                >
                  View All Courses
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Why Learn Farsi Fast Section */}
        <div className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Why Learn Farsi Fast?
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Persian is one of the world's most beautiful languages, spoken by over 110 million people. 
                Learning it quickly opens doors to rich culture, literature, and business opportunities.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="text-center">
                <FaGlobe className="w-12 h-12 text-blue-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">Global Reach</h3>
                <p className="text-gray-600">Connect with Persian speakers in Iran, Afghanistan, Tajikistan, and worldwide</p>
              </div>
              <div className="text-center">
                <FaHeart className="w-12 h-12 text-red-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">Rich Culture</h3>
                <p className="text-gray-600">Access centuries of poetry, literature, and cultural heritage</p>
              </div>
              <div className="text-center">
                <FaGraduationCap className="w-12 h-12 text-green-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">Career Growth</h3>
                <p className="text-gray-600">Enhance your professional opportunities in international business</p>
              </div>
              <div className="text-center">
                <FaLightbulb className="w-12 h-12 text-yellow-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">Cognitive Benefits</h3>
                <p className="text-gray-600">Improve memory and cognitive flexibility through language learning</p>
              </div>
            </div>
          </div>
        </div>

        {/* Fast Learning Tips */}
        <div className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Proven Methods to Learn Farsi Fast
              </h2>
              <p className="text-xl text-gray-600">
                Our accelerated learning approach combines science-backed techniques with cultural immersion
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8">
              {fastLearningTips.map((tip, index) => (
                <div key={index} className="bg-white rounded-lg p-8 shadow-lg">
                  <div className="flex items-center mb-4">
                    {tip.icon}
                    <h3 className="text-xl font-semibold ml-3">{tip.title}</h3>
                  </div>
                  <p className="text-gray-600 mb-4">{tip.description}</p>
                  <ul className="space-y-2">
                    {tip.tips.map((tipItem, tipIndex) => (
                      <li key={tipIndex} className="flex items-center text-sm text-gray-700">
                        <FaCheck className="w-4 h-4 text-green-500 mr-2" />
                        {tipItem}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* 30-Day Learning Path */}
        <div className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Your 30-Day Farsi Learning Path
              </h2>
              <p className="text-xl text-gray-600">
                A structured approach to get you speaking Persian confidently in just one month
              </p>
            </div>
            
            <div className="space-y-8">
              {learningPath.map((phase, index) => (
                <div key={index} className="bg-gray-50 rounded-lg p-8">
                  <div className="flex items-center mb-4">
                    <div className="bg-blue-600 text-white rounded-full w-12 h-12 flex items-center justify-center font-bold text-lg mr-4">
                      {index + 1}
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900">{phase.week}</h3>
                      <p className="text-lg text-blue-600 font-semibold">{phase.focus}</p>
                    </div>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-8">
                    <div>
                      <h4 className="text-lg font-semibold mb-3 text-gray-800">Goals</h4>
                      <ul className="space-y-2">
                        {phase.goals.map((goal, goalIndex) => (
                          <li key={goalIndex} className="flex items-center text-gray-700">
                            <FaCheck className="w-4 h-4 text-green-500 mr-2" />
                            {goal}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold mb-3 text-gray-800">Resources</h4>
                      <ul className="space-y-2">
                        {phase.resources.map((resource, resourceIndex) => (
                          <li key={resourceIndex} className="flex items-center text-gray-700">
                            <FaBookOpen className="w-4 h-4 text-blue-500 mr-2" />
                            {resource}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Success Stories */}
        <div className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Success Stories
              </h2>
              <p className="text-xl text-gray-600">
                Real learners who achieved their Persian language goals quickly
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8">
              {successStories.map((story, index) => (
                <div key={index} className="bg-white rounded-lg p-6 shadow-lg">
                  <div className="flex items-center mb-4">
                    <div className="bg-blue-100 rounded-full p-3 mr-4">
                      <FaStar className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold">{story.name}</h3>
                      <p className="text-blue-600 font-medium">{story.timeframe} • {story.achievement}</p>
                    </div>
                  </div>
                  <p className="text-gray-600 italic">"{story.story}"</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="py-16 bg-blue-600">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Ready to Learn Farsi Fast?
            </h2>
            <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
              Join thousands of learners who have accelerated their Persian language journey with our proven method
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/register" 
                className="px-8 py-4 bg-white text-blue-600 font-bold rounded-lg hover:bg-gray-100 transition duration-300 text-lg"
              >
                Start Your 30-Day Journey
              </Link>
              <Link 
                to="/pathways" 
                className="px-8 py-4 border-2 border-white text-white font-bold rounded-lg hover:bg-white hover:text-blue-600 transition duration-300 text-lg"
              >
                Explore All Courses
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default LearnFarsiFast;
